import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { ConfirmationService, MessageService, PrimeNGConfig, SelectItem, SelectItemGroup } from 'primeng/api';
import { TokenService } from './token.service';
import * as moment from 'moment-timezone';
import * as FileSaver from 'file-saver';
import { Subscription } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { CountryModel } from 'projects/core/src/lib/models/country.model';

export interface ResponseErrorType {
  status: number;
  message?: string;
  fielderrors?: any;
}

@Injectable({
  providedIn: 'root'
})
export class HelperService {

  constructor(
    private router: Router,
    private messageService: MessageService,
    private token: TokenService,
    private confirmation: ConfirmationService,
    private transloco: TranslocoService,
    private primeConfig: PrimeNGConfig,
    @Inject(DOCUMENT) private document: Document
  ) { }

  public isAuthorized(statuscode: any): boolean {
    return statuscode !== 401 && statuscode !== 403;
  }

  public throwApiError(error: HttpErrorResponse): ResponseErrorType {
    const result = {
      status: error.status,
      message: error.error?.message || null
    };
    switch (error.status) {
      case (401): {
        this.confirmation.confirm({
          icon: 'pi pi-ban',
          header: this.transloco.translate('common.error'),
          message: this.transloco.translate('auth.session_expired'),
          acceptLabel: this.transloco.translate('common.labels.to_login'),
          acceptIcon: 'pi pi-sign-out',
          rejectVisible: false,
          closeOnEscape: false,
          accept: () => {
            this.token.removeToken();
            this.router.navigate(['login']);
          },
          reject: () => {
            this.token.removeToken();
            this.router.navigate(['login']);
          }
        });
        break;
      }
      case (403): {
        this.messageService.add({
          key: 'toast',
          severity: 'error',
          summary: this.transloco.translate('common.error'),
          detail: this.transloco.translate('backend.exceptions.not_authorized')
        });
        this.router.navigate(['dashboard']);
        break;
      }
      case (404): {
        this.messageService.add({
          key: 'toast',
          severity: 'error',
          summary: this.transloco.translate('common.error'),
          detail: this.transloco.translate('backend.exceptions.resource_not_found')
        });
        this.router.navigate(['dashboard']);
        break;
      }
      case (500): {
        this.messageService.add({
          key: 'toast',
          severity: 'error',
          summary: this.transloco.translate('common.error'),
          detail: this.transloco.translate('backend.exceptions.technical_server_error')
        });
        this.router.navigate(['dashboard']);
        break;
      }
      default: {
        result['fielderrors'] = error.error?.fielderrors || null;
        break;
      }
    }
    return result;
  }

  public setLanguage(language: string): void {
    language = language.toLowerCase();
    this.document.documentElement.lang = language;
    this.transloco.setActiveLang(language);
    this.primeConfig.setTranslation(this.transloco.translateObject('primeng'));
  }

  public getTimezoneName(timezone: string, time?: Date): string {
    const zonesToTranslate = ['CET', 'CEST'];
    let result = '';
    if (!timezone) {
      timezone = 'Europe/Berlin';
    }
    if (time) {
      result = moment.tz(time, timezone).format('z');
    } else {
      result = moment.tz(timezone).format('z');
    }
    if (zonesToTranslate.indexOf(result) !== -1 && this.transloco.getActiveLang() !== this.transloco.getDefaultLang() && this.transloco.translate('timezones.' + result) !== 'timezones.' + result) {
      result = this.transloco.translate('timezones.' + result);
    }
    return result;
  }

  public saveAsExcelFile(buffer: any, fileName: string): void {
    const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const EXCEL_EXTENSION = '.xlsx';
    const data: Blob = new Blob([buffer], {
        type: EXCEL_TYPE
    });
    FileSaver.saveAs(data, fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION);
  }

  public getFileSize(size: number): any {
    const fSExt = ['Bytes', 'KB', 'MB', 'GB'];
    let i = 0;
    while (size > 900) {
      size /= 1024;
      i++;
    }
    return (Math.round(size * 100) / 100) + ' ' + fSExt[i];
  }

  public getFileExtension(filename: string): string | null {
    const parts = filename.split('.');
    if (Array.isArray(parts) && parts.length > 0) {
      return parts[parts.length - 1];
    }
    return null;
  }

  public unsetSubscriptions(subscriptions: Array<Subscription>): void {
    if (Array.isArray(subscriptions) && subscriptions.length > 0) {
      subscriptions.forEach((subscription: Subscription) => {
        subscription.unsubscribe();
      });
    }
    subscriptions = [];
  }

  public getGroupOptions(entries: Array<any>, value: string, childsPropName: string, id: string = 'id'): Array<SelectItemGroup> {
    const options: Array<SelectItemGroup> = [];
    if (Array.isArray(entries) && entries?.length > 0) {
      entries = entries.sort((a: any, b: any) => {
        return (a[value] > b[value]) ? 1 : ((b[value] > a[value]) ? -1 : 0);
      });
      entries.forEach((entry: any) => {
        const option = {
          value: entry[id],
          label: entry[value],
          items: []
        };
        if (Array.isArray(entry[childsPropName])) {
          option.items = entry[childsPropName].sort((a: any, b: any) => {
            return (a[value] > b[value]) ? 1 : ((b[value] > a[value]) ? -1 : 0);
          }).map((childunit: any) => {
            return {
              value: childunit[id],
              label: childunit[value]
            };
          });
        }
        options.push(option);
      });
    }
    return options;
  }

  public getFromGroupedOptions(options: Array<SelectItemGroup>, value: any): SelectItem | null {
    let result = null;
    options.forEach((option: SelectItemGroup) => {
      if (option.value === value) {
        result = option;
      } else if (option.items?.length > 0) {
        option.items.forEach((item: SelectItem) => {
          if (item.value === value) {
            result = item;
          }
        });
      }
    });
    return result;
  }

  public getSortValue(a: string, b: string, options: any = {}): number {
    const aValue = (a) ? a.toLowerCase() : '';
    const bValue = (b) ? b.toLowerCase() : '';
    return aValue.localeCompare(bValue, this.transloco.getActiveLang().toLowerCase(), options);
  }

  public getCountryOptions(data: Array<any>): Array<CountryModel> {
    const isEnglisch = this.transloco.getActiveLang() === 'en';
    let countries = [];
    if (Array.isArray(data)) {
      countries = data.map((country: any) => {
        country = new CountryModel().deserialize(country);
        if (isEnglisch === false) {
          const countryName = this.transloco.translate('countries.' + country.country_code);
          if (countryName !== country.country_name) {
            country.country_name = countryName;
          }
        }
        return country;
      }).sort((a: CountryModel, b: CountryModel) => {
        const prioA = (a.prio !== null) ? a.prio : 10000;
        const prioB = (b.prio !== null) ? b.prio : 10000;
        if (prioA === prioB) {
          if (a.country_name < b.country_name) { return -1; }
          if (a.country_name > b.country_name) { return 1; }
          return 0;
        } else {
          return prioA - prioB;
        }
      });
      const pos = countries.findIndex((country: CountryModel) => {
        return country.prio === null;
      });
      countries.splice(pos, 0, new CountryModel().deserialize({
        country_code: 'XX',
        country_name: '____________________________',
        disabled: true
      }));
    }
    return countries;
  }

}
